import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { ApiService } from './api.service';
import { OrganizationStateService } from './organization-state.service';

@Injectable({ providedIn: 'root' })
export class CustomTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly apiService = inject(ApiService);
  private readonly organizationStateService = inject(OrganizationStateService);
  private lastBaseTitle = '';

  constructor() {
    super();
    // Subscribe to organization state updates to refresh title dynamically
    this.organizationStateService.organization$.subscribe(() => {
      this.setFormattedTitle(this.lastBaseTitle);
    });
  }

  override updateTitle(routerState: RouterStateSnapshot) {
    const title = this.buildTitle(routerState);
    if (title) {
      this.lastBaseTitle = title;
    } else {
      // Fallback: dynamically extract title from URL path
      const url = routerState.url;
      const titleFromUrl = this.getTitleFromUrl(url);
      this.lastBaseTitle = titleFromUrl || '';
    }

    this.setFormattedTitle(this.lastBaseTitle);
  }

  /**
   * Formats the browser title dynamically without any hardcoding:
   * Pattern: [Page Title] - [Org Name / Parking Name]
   */
  private setFormattedTitle(baseTitle: string) {
    const contextName = this.getContextName();

    if (baseTitle && contextName && baseTitle.toLowerCase() !== contextName.toLowerCase()) {
      this.title.setTitle(`${baseTitle} - ${contextName}`);
    } else if (baseTitle) {
      this.title.setTitle(baseTitle);
    } else if (contextName) {
      this.title.setTitle(contextName);
    }
  }

  private getContextName(): string | null {
    try {
      const pov = this.apiService.getUserPov();
      const currentUser = this.apiService.getCurrentUser();

      if (pov === 'parking') {
        // 1. Try selectedParkingName from storage
        const selectedParkingName = sessionStorage.getItem('selectedParkingName') || localStorage.getItem('selectedParkingName');
        if (selectedParkingName && selectedParkingName.trim() && !selectedParkingName.startsWith('Loading')) {
          return selectedParkingName.trim();
        }

        // 2. Try vendor selected parking in localStorage
        const vendorDataStr = localStorage.getItem('selectedParkingForVendor');
        if (vendorDataStr) {
          try {
            const vendorData = JSON.parse(vendorDataStr);
            const pName = vendorData?.parkingName || vendorData?.name || vendorData?.parkingData?.name;
            if (pName && typeof pName === 'string' && pName.trim()) {
              return pName.trim();
            }
          } catch (e) {}
        }

        // 3. Try parking team member data
        const parkingTeamStr = localStorage.getItem('parking_team_member_data');
        if (parkingTeamStr) {
          try {
            const pData = JSON.parse(parkingTeamStr);
            const pName = pData?.parking_name || pData?.parkingName || pData?.name;
            if (pName && typeof pName === 'string' && pName.trim()) {
              return pName.trim();
            }
          } catch (e) {}
        }

        // 4. Try user.parkings array or currentUser properties
        if (currentUser) {
          const currentParkingId = this.apiService.getCurrentParkingId();
          if (currentUser.parkings && Array.isArray(currentUser.parkings) && currentUser.parkings.length > 0) {
            if (currentParkingId) {
              const matched = currentUser.parkings.find((p: any) => p.id === currentParkingId || p._id === currentParkingId);
              if (matched?.name) return matched.name.trim();
            }
            if (currentUser.parkings[0]?.name) {
              return currentUser.parkings[0].name.trim();
            }
          }

          const directPName = currentUser.parking_name || currentUser.parkingName;
          if (directPName && typeof directPName === 'string' && directPName.trim()) {
            return directPName.trim();
          }
        }
      }

      // Organization POV or fallback to Organization Name
      const orgState = this.organizationStateService.getCurrentOrganization();
      if (orgState?.name && orgState.name.trim()) {
        return orgState.name.trim();
      }
      if (orgState?.company && orgState.company.trim()) {
        return orgState.company.trim();
      }

      if (currentUser) {
        const orgName = currentUser.company ||
          currentUser.organization_name ||
          currentUser.organizationName ||
          currentUser.orgName ||
          currentUser.organization?.name;

        if (orgName && typeof orgName === 'string' && orgName.trim()) {
          return orgName.trim();
        }
      }

      // Try stored organization/company name in localStorage
      const storedOrgName = localStorage.getItem('organization_name') || localStorage.getItem('company_name');
      if (storedOrgName && storedOrgName.trim()) {
        return storedOrgName.trim();
      }
    } catch (e) {
      console.error('Error resolving title context name:', e);
    }

    return null;
  }

  private getTitleFromUrl(url: string): string | null {
    if (!url || url === '/') {
      return null;
    }

    // Split URL and filter out IDs, parameters, and empty segments
    const segments = url.split('?')[0].split('/');
    const cleanSegments = segments.filter(segment => {
      if (!segment) return false;
      // Skip IDs or dynamic path variables (hex, numeric, uuid, orgId, parkingId etc.)
      const isNumeric = /^\d+$/.test(segment);
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
      const isHexId = /^[0-9a-fA-F]{24}$/.test(segment);
      const isParam = segment.startsWith(':');
      // Skip common structural prefixes
      const isSkipPrefix = segment === 'parking' || segment === 'organization' || segment === 'org' || segment === 'pos' || segment === 'admin' || segment === 'help-center' || segment === 'settings';
      return !isNumeric && !isUUID && !isHexId && !isParam && !isSkipPrefix;
    });

    if (cleanSegments.length === 0) {
      return null;
    }

    // Get the last valid segment
    const lastSegment = cleanSegments[cleanSegments.length - 1];

    // Format dynamically: dash/underscore to space, and title-case words
    return lastSegment
      .split(/[-_]/)
      .map(word => {
        const lower = word.toLowerCase();
        if (lower === 'ai') return 'AI';
        if (lower === 'pos') return 'POS';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }
}
